import { useCallback, useMemo, useState } from "react";

import type { ContentBlock } from "../blockTypes";
import type { BlockId, LessonScreenData } from "../lessonTypes";
import { buildScreens } from "../utils/buildScreens";

// Only these blocks gate navigation. Question blocks never block the learner,
// so a wrong answer can't get them stuck.
const REQUIRED_BLOCK_TYPES: string[] = ["interactive"];

const isRequiredBlock = (block: ContentBlock): boolean => REQUIRED_BLOCK_TYPES.includes(block.type);

// Manages lesson screens, per-screen block completion, navigation and progress.
// State resets when the components using it remounts (see `key` in ContentPage).
export const useLessonProgress = (fragments?: unknown[]) => {
    const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
    const [completedBlocks, setCompletedBlocks] = useState<Record<number, Set<BlockId>>>({});

    // Becomes true when the learner finishes the last screen
    const [isFinished, setIsFinished] = useState(false);

    const screens = useMemo<LessonScreenData[]>(
        () => (fragments ? buildScreens(fragments) : []),
        [fragments]
    );

    const currentScreen: LessonScreenData | undefined = screens[currentScreenIndex];
    const isLastScreen = currentScreenIndex === screens.length - 1;

    // A screen is complete when all its required blocks are done
    // (screens without required blocks are complete immediately)
    const isScreenComplete = useMemo(() => {
        if (!currentScreen) return false;

        const done = completedBlocks[currentScreenIndex];
        return currentScreen.blocks
            .filter(isRequiredBlock)
            .every((block) => block.id !== undefined && !!done?.has(block.id));
    }, [currentScreen, completedBlocks, currentScreenIndex]);

    const handleBlockComplete = useCallback(
        (blockId: BlockId) => {
            setCompletedBlocks((previous) => ({
                ...previous,
                [currentScreenIndex]: new Set(previous[currentScreenIndex]).add(blockId)
            }));
        },
        [currentScreenIndex]
    );

    const goNext = () => {
        if (!isScreenComplete) return;

        // Last screen: finish the lesson instead of moving to another screen
        if (isLastScreen) {
            setIsFinished(true);
            return;
        }

        setCurrentScreenIndex((index) => index + 1);
    };

    const goPrevious = () => {
        if (currentScreenIndex <= 0) return;
        setCurrentScreenIndex((index) => index - 1);
    };

    const progress = isFinished
        ? 100
        : screens.length
            ? ((currentScreenIndex + 1) / screens.length) * 100
            : 0;

    return {
        currentScreen,
        currentScreenIndex,
        isScreenComplete,
        isLastScreen,
        isFinished,
        progress,
        handleBlockComplete,
        goNext,
        goPrevious
    };
};